
import { Options, Vue } from "vue-class-component";
import { ListPickupController } from "@/app/ui/controllers/ListPickupController";
import { ListPickupV2Controller } from "@/app/ui/controllers/ListPickupV2Controller";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import { AccountController } from "@/app/ui/controllers/AccountController";
import PickupDetailSttTable from "./components/pickup-detail-stt-table.vue";
import {
  ListPickupDetail,
  CancelPickupApiRequest
} from "@/domain/entities/ListPickupDetailV2";
import { RequestListPickupDetail } from "@/domain/entities/ListPickupV2";
import { ApiStateData } from "@/domain/entities/Api";
import { ModalMessageEntities } from "@/domain/entities/MainApp";
import { FlagsPermissionPickup } from "@/feature-flags/flag-pickup";
import PickUpDetailHeader from "./components/pickup-detail-header.vue";
import PickupDetailSummaryRow from "./components/pickup-detail-summary-row/index.vue";
import PickupCancelConfirmModal from "./components/pickup-cancel-confirm-modal.vue";
import parsingErrorResponse from "@/app/infrastructures/misc/common-library/ParsingErrorResponse";
import PickupDetailSidebar from "./components/pickup-detail-sidebar.vue";
import PickupDetailRequestInfo from "./components/pickup-detail-request-info/index.vue";
import openLabelPdfWithAuth from "../../common/openLabelPdfWithAuth";

@Options({
  components: {
    PickUpDetailHeader,
    PickupDetailSttTable,
    PickupDetailSummaryRow,
    PickupCancelConfirmModal,
    PickupDetailSidebar,
    PickupDetailRequestInfo
  }
})
export default class DetailPickup extends Vue {
  detailController = ListPickupV2Controller;
  cancelController = ListPickupController;
  apiDetailData = new ApiStateData();
  detailData = new ListPickupDetail();
  phoneRevealed = false;
  isConfirmModalOpen = false;
  isSubmitting = false;

  mounted(): void {
    this.fetchDetail();
  }

  get id(): string {
    return String(this.$route.params.id || "");
  }

  get pickupId(): string {
    return this.detailData.shipmentId || String(this.id || "");
  }

  get canShowCancelButton(): boolean {
    const isFlagActive = FlagsPermissionPickup.permission_pickup_list_pickup_cancel.isEnabled();
    const isDataLoaded = !this.isLoading && !!this.detailData?.shipmentId;
    const isCancelable = !!this.detailData?.isCancelable;
    return isFlagActive && isCancelable && isDataLoaded;
  }

  get targetShipmentId(): string {
    return this.detailData?.shipmentId || String(this.id || "");
  }

  get summaryTotalRequestProps() {
    return {
      koliCount: this.detailData.estimateTotalKoli,
      weightKg: this.detailData.estimateTotalTonase,
      pickupDateLabel: this.detailData.summaryPickupDateLabel,
      pickupTimeLabel: this.detailData.summaryPickupTimeLabel,
      additionalKoli: this.detailData.additionalKoli
    };
  }

  get summaryTotalPickupProps() {
    return {
      sttCount: this.detailData.actualTotalShipment,
      koliCount: this.detailData.actualTotalKoli,
      weightKg: this.detailData.actualTotalTonase,
      pickupDateLabel: this.detailData.formattedActualPickupDateTime,
      pickupTimeLabel: this.detailData.summaryPickupTimeLabel,
      additionalKoli: this.detailData.additionalKoli
    };
  }

  get summaryAgentHubProps() {
    return {
      sttCount: this.detailData.agentHubTotalShipment,
      koliCount: this.detailData.agentHubTotalKoli,
      weightKg: this.detailData.agentHubTotalTonase
    };
  }

  get sidebarProps() {
    return {
      statusCode: this.detailData.latestStatus,
      createdAtLabel: this.detailData.createdAtSidebarLabel,
      createdByLabel: this.detailData.createdBy,
      courierAvailable: this.detailData.courierAvailable,
      courierName: this.courierNameDisplay,
      courierId: this.detailData.courierId,
      courierPhoneDisplay: this.detailData.courierPhone,
      pickupProofUrls: this.detailData.pickupProof,
      showClientHeading: !!AccountController.accountData?.isInternalAccount,
      clientRefDisplay: this.detailData.clientRef || "-"
    };
  }

  get mergedShipments() {
    if (this.detailData.mergedFromShipments.length) {
      return this.detailData.mergedFromShipments.map((item) => ({
        shipmentId: item.shipmentId,
        koliCount: item.totalKoli,
        weightKg: item.totalWeight
      }));
    }

    return this.detailData.mergedFromShipmentIds.map((shipmentId) => ({
      shipmentId,
      koliCount: 0,
      weightKg: 0
    }));
  }

  openCancelModal(): void {
    this.isConfirmModalOpen = true;
  }

  get courierNameDisplay(): string {
    const name = this.detailData.courierName;
    const showDhlPrefix =
      this.detailData.isDhlFirstMilePrintEnabled &&
      this.detailData.courierAvailable &&
      Boolean(name?.trim());
    if (!showDhlPrefix) return name;
    return `${this.$t("pickup.detail.dhlCourierNamePrefix")} ${name}`;
  }

  async handleCancelSubmit(reason: string): Promise<void> {
    if (!this.targetShipmentId) return;

    this.isSubmitting = true;

    try {
      const payload = new CancelPickupApiRequest({
        shipmentId: this.targetShipmentId,
        cancelNote: reason
      });

      await this.cancelController.cancelPickup(payload);

      this.isConfirmModalOpen = false;
      this.showSuccessModal();
    } catch (err) {
      this.isConfirmModalOpen = false;
      this.showFailedModal(err, reason);
    } finally {
      this.fetchDetail();
      this.isSubmitting = false;
    }
  }

  showSuccessModal(): void {
    MainAppController.showMessageModal(
      new ModalMessageEntities({
        image: "badge-success",
        title: (this as any).$t("pickup.cancel.success.title"),
        message: (this as any).$t("pickup.cancel.success.body"),
        textSuccess: (this as any).$t("pickup.cancel.success.close"),
        customButton: "px-8 w-300px",
        onSubmit: async () => {
          MainAppController.closeMessageModal();
        }
      })
    );
  }

  showFailedModal(err: any, reason: string): void {
    const failedTitle = (this as any).$t("pickup.cancel.failed.title", {
      shipmentId: this.pickupId
    });

    const errorEntity = parsingErrorResponse(
      err,
      failedTitle,
      () => {
        MainAppController.closeErrorMessage();
        this.handleCancelSubmit(reason);
      },
      () => {
        MainAppController.closeErrorMessage();
      }
    );

    MainAppController.showErrorMessage(errorEntity);
  }

  goBack(): void {
    this.$router.push("/pickup/list-pickup-v2");
  }

  onPrintLabel(): void {
    const tab = window.open("about:blank", "_blank");
    openLabelPdfWithAuth(this.detailData.dhlLabelPrintUrl, tab);
  }

  async fetchDetail(): Promise<boolean> {
    return this.loadDetail(this.phoneRevealed);
  }

  async onRevealPhone(): Promise<void> {
    if (this.phoneRevealed || this.isLoading) {
      await this.loadDetail(false);
      this.phoneRevealed = false;
      return
    }
    const isSuccess = await this.loadDetail(true);
    if (isSuccess) {
      this.phoneRevealed = true;
    }
  }

  private async loadDetail(showDetails: boolean): Promise<boolean> {
    this.apiDetailData.loading = true;

    try {
      const detail = await this.detailController.getListPickupDetail({
        id: this.id,
        params: new RequestListPickupDetail({ isShowDetails: showDetails })
      });
      this.detailData = new ListPickupDetail({
        ...detail,
        countryCode: AccountController.accountData?.countryCode.toUpperCase() || "ID"
      });
      this.apiDetailData.errorType = "";
      return true;
    } catch (error) {
      const errorType = parsingErrorResponse(error).type;
      this.apiDetailData.errorType =
        errorType === "internet-error" ? "internet" : errorType;
      return false;
    } finally {
      this.apiDetailData.loading = false;
    }
  }

  get isLoading(): boolean {
    return this.apiDetailData.loading;
  }

  get isError(): boolean {
    return !!this.apiDetailData.errorType;
  }

  get errorCause(): string {
    return this.apiDetailData.errorType;
  }

  get isPcaEmpty() {
    return this.detailData.isPcaEmpty;
  }

  get requestInfoCorporateAddress() {
    const address = this.detailData.pickupCorporateAddress;
    return {
      name: address.pcaName,
      picName: address.pcaPicName,
      phoneNumber: address.pcaPhoneNumber,
      address: address.pcaAddress,
      latLon: address.pcaLatlon
    };
  }
}
